import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Avatar, Buttons, CancelButton, DeleteConfirmation, Drawer, Table } from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';

const Description = styled.p`
  margin-bottom: 1.5rem;
`;

const Member = styled.div`
  display: flex;
  align-items: center;
`;

const MemberInfo = styled.div`
  display: flex;
  margin-left: 0.5rem;
`;

export default function TeamsMembersDrawer({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const confirmation = useConfirmation();
  const isMounted = useIsMounted();
  const [members, setMembers] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).integrations().microsoft.teams.getMembers();

    if (!isMounted.current) return;
    setMembers(data.members.filter((member) => member.link));
  }, [api, workspace.id, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRemove = async (member) => {
    const confirmed = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve} title="Remove Microsoft Teams Link" deleteLabel="Remove">
        Remove the Microsoft Teams link for {member.name}? They&apos;ll be asked to link their Ruddr account again the
        next time they message Ruddr in Teams.
      </DeleteConfirmation>
    ));
    if (!confirmed) return;

    try {
      await api.www.workspaces(workspace.id).integrations().microsoft.teams.removeLink(member.id);
      await fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Drawer isOpen title="Microsoft Teams Members" width="60rem" onClose={onClose}>
      {(closeDrawer) => {
        if (!members) return <PageLoader />;

        return (
          <>
            <Description>
              Members who have linked their Ruddr account to Microsoft Teams. Members link from the Ruddr bot in Teams;
              removing a link here disconnects them until they link again.
            </Description>

            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column>Member</Table.Column>
                <Table.Column>Microsoft Account</Table.Column>
                <Table.BoxActionsColumn />
              </Table.BoxHeader>

              <Table.Body>
                {members.map((member) => (
                  <Table.BoxRow key={member.id} data-testid={`ms-teams-member-${member.id}`}>
                    <Table.Cell>
                      <Member>
                        <Avatar value={member} isCircle hasBackground initialsFontSize=".9rem" />
                        <MemberInfo>{member.name}</MemberInfo>
                      </Member>
                    </Table.Cell>
                    <Table.Cell>{member.link.account}</Table.Cell>

                    <TableBoxRowActions>
                      <TableBoxRowActions.Delete tooltip="Remove link" onClick={() => handleRemove(member)} />
                    </TableBoxRowActions>
                  </Table.BoxRow>
                ))}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={closeDrawer}>Close</CancelButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
